import { Clock, Download, History, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { cn } from '@/lib/utils';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

/**
 * Prior polished exports kept when the AI editor re-cuts the same clips.
 */
export function ReelsShortsRenderVersions({
  versions = [],
  onRestore = null,
  restoringId = null,
  disabled = false,
  compact = false,
}) {
  const rows = Array.isArray(versions) ? versions.filter((v) => v?.url) : [];
  if (!rows.length) return null;

  return (
    <div
      className={cn(
        'rounded-xl border border-[#1f2635] bg-[#0d1017] space-y-2',
        compact ? 'p-2.5' : 'p-3',
      )}
      data-testid="reels-shorts-render-versions"
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        <History className="w-3 h-3" />
        Export history
        <span className="normal-case tracking-normal text-slate-600">· {rows.length}</span>
      </p>
      <ul className="space-y-1.5 max-h-48 overflow-y-auto">
        {rows.map((row, idx) => {
          const url = mediaUrlForPlayback(row.url);
          const busy = restoringId === row.id;
          const active = Boolean(row.active);
          return (
            <li
              key={row.id || `${row.url}-${idx}`}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-2 py-1.5',
                active ? 'border-rose-500/35 bg-rose-500/10' : 'border-transparent bg-white/[0.03]',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-slate-200 truncate">
                  {row.label || `Version ${rows.length - idx}`}
                  {active ? (
                    <span className="ml-1.5 text-[9px] uppercase tracking-wide text-rose-300">current</span>
                  ) : null}
                </p>
                <p className="text-[10px] text-slate-500 flex items-center gap-1 truncate">
                  <Clock className="w-2.5 h-2.5 shrink-0" />
                  {formatWhen(row.created_at)}
                  {row.source ? ` · ${row.source}` : ''}
                </p>
              </div>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-rose-200 hover:bg-white/5"
                  title="Open / download"
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
              ) : null}
              {onRestore && !active ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={disabled || busy || Boolean(restoringId)}
                  onClick={() => onRestore(row)}
                  className="h-7 px-2 text-[10px] text-slate-300"
                  data-testid={`reels-shorts-restore-version-${row.id}`}
                >
                  {busy ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Restore
                    </>
                  )}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default ReelsShortsRenderVersions;
